import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Zoom,
  Stack
} from '@mui/material';
import {
  Menu,
  Add,
  ChevronLeft,
  Article,
  Desk,
  EmojiEvents,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import Cookies from 'js-cookie';
import SchoolIcon from '@mui/icons-material/School';
import { useThemeContext } from '../contexts/ThemeContext.tsx';

// --- MODERN STÍLUSOZOTT KOMPONENSEK ---

const AnimatedDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)',
    backgroundColor: darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.4)',
  },
  '& .MuiPaper-root': {
    borderRadius: '24px',
    border: darkMode ? '1px solid rgba(255, 0, 0, 0.2)' : '1px solid rgba(33, 203, 243, 0.3)',
    background: darkMode 
        ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
        : 'linear-gradient(45deg, #f0f4ff 30%, #c2e9fb 90%)',
    backgroundSize: '200% 200%',
    color: darkMode ? 'white' : '#1a237e',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
  },
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(128,128,128,0.2)',
    },
    '&:hover fieldset': {
      borderColor: darkMode ? '#ff4081' : '#2196f3',
    },
    '&.Mui-error fieldset': {
      borderColor: '#f44336',
    }
  },
  '& .MuiInputLabel-root': {
    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
  }
}));

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'darkMode',
})<{ darkMode: boolean }>(({ darkMode }) => ({
  borderRadius: '20px',
  textTransform: 'none',
  padding: '10px 32px',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
  },
  '&:disabled': {
    backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    color: 'rgba(128,128,128,0.5)',
  }
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.appBar - 1,
  [theme.breakpoints.up('sm')]: {
    width: open ? 240 : 56,
    '& .MuiDrawer-paper': {
      width: open ? 240 : 56,
      top: '68px',
      height: 'calc(100vh - 68px)',
      position: 'fixed',
      overflowX: 'hidden',
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  },
  [theme.breakpoints.down('sm')]: {
    height: '56px',
    width: '100%',
    '& .MuiDrawer-paper': {
      top: '56px',
      height: '56px',
      width: '100%',
      flexDirection: 'row',
      overflow: 'hidden',
      borderRight: 'none',
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const NavListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isMobile',
})<{ isActive: boolean; isMobile: boolean }>(({ theme, isActive, isMobile }) => ({
  justifyContent: 'center',
  minHeight: 48,
  px: 2.5,
  backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
  color: isActive ? theme.palette.primary.main : 'inherit',
  ...(isMobile ? {
    px: 1,
    minHeight: '56px',
    width: '100%',
    borderBottom: isActive ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
  } : {
    borderLeft: isActive ? `4px solid ${theme.palette.primary.main}` : '4px solid transparent',
  })
}));

const CollapsedListItemIcon = styled(ListItemIcon, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  minWidth: 0,
  justifyContent: 'center',
  color: isActive ? theme.palette.primary.main : 'inherit',
  transform: isActive ? 'scale(1.1)' : 'scale(1)',
}));

// --- KOMPONENS ---

interface LeftPanelProps {
  isAuthenticated: boolean;
  onLoginRequired: () => void;
  onNavigation: (path: string) => void;
  open: boolean;
  onToggle: () => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({ isAuthenticated, onLoginRequired, onNavigation, open, onToggle }) => {
  const { darkMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [submissionError, setSubmissionError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [formData, setFormData] = useState({ title: '', category: '', shortDescription: '', url: '' });
  const [urlError, setUrlError] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  // URL Validáló függvény (Regex)
  const isValidUrl = (url: string) => {
    const pattern = new RegExp('^(https?:\\/\\/)?' + 
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + 
      '((\\d{1,3}\\.){3}\\d{1,3}))' + 
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + 
      '(\\?[;&a-z\\d%_.~+=-]*)?' + 
      '(\\#[-a-z\\d%_.~+ Barb-]*)*$', 'i');
    return !!pattern.test(url);
  };

  const handleNavigation = (path: string) => {
    if (!isAuthenticated && path !== '/learning' && path !== '/topcontents') {
      onLoginRequired();
    } else {
      onNavigation(path);
    }
  };

  const handleOpenDialog = () => {
    if (!isAuthenticated) {
      onLoginRequired();
      return;
    }
    setSubmissionSuccess(false);
    setSubmissionError('');
    setUrlError(false);
    setIsPrivate(false); // Reseteljük a kapcsolót megnyitáskor
    setDialogOpen(true);
  };

  const handleSubmitContent = async () => {
    if (!isValidUrl(formData.url)) {
      setUrlError(true);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/content/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${Cookies.get('token')}`
        },
        body: JSON.stringify({ ...formData, status: isPrivate ? "PRIVATE" : "PENDING" })
      });
      const responseData = await response.json();
      if (response.ok && !responseData.error) {
        setFormData({ title: '', category: '', shortDescription: '', url: '' });
        setSubmissionSuccess(true);
      } else {
        setSubmissionError(responseData.message || 'Submission failed');
      }
    } catch (error) {
      setSubmissionError('An unexpected error occurred.');
    }
  };

  const menuItems = [
    { icon: <Add />, text: "Create", action: handleOpenDialog, path: null },
    { icon: <SchoolIcon />, text: "Learning", path: '/learning' },
    { icon: <Article />, text: "My Content", path: '/mycontents' },
    { icon: <Article />, text: "Private", path: '/myprivatecontents' },
    { icon: <Desk />, text: "Study Desk", path: '/mystudydesk' },
    { icon: <EmojiEvents />, text: "Top", path: '/topcontents' },
  ];

  return (
    <>
      <StyledDrawer variant="permanent" open={open}>
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: open ? 'flex-end' : 'center', p: 1, minHeight: '48px' }}>
            <IconButton onClick={onToggle}>
              {open ? <ChevronLeft /> : <Menu />}
            </IconButton>
          </Box>
        )}

        <List sx={{ display: isMobile ? 'flex' : 'block', flexDirection: isMobile ? 'row' : 'column', width: '100%', p: 0, justifyContent: isMobile ? 'space-around' : 'flex-start' }}>
          {menuItems.map((item, index) => {
            const isActive = item.path ? location.pathname === item.path : false;
            return (
              <ListItem key={index} disablePadding sx={{ display: 'block', width: isMobile ? 'auto' : '100%' }}>
                <NavListItemButton isActive={isActive} isMobile={isMobile} onClick={() => item.action ? item.action() : handleNavigation(item.path!)}>
                  <CollapsedListItemIcon isActive={isActive}>{item.icon}</CollapsedListItemIcon>
                  {!isMobile && open && (
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 'bold' : 'normal', color: isActive ? 'primary.main' : 'inherit' }} sx={{ ml: 2 }} />
                  )}
                </NavListItemButton>
              </ListItem>
            );
          })}
        </List>
      </StyledDrawer>

      <AnimatedDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        darkMode={darkMode}
        TransitionComponent={Zoom}
        transitionDuration={400}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3, fontWeight: 'bold', fontSize: '1.5rem' }}>
          {submissionSuccess 
            ? (isPrivate ? '🔒 Secured!' : '🎉 Success!') 
            : '🚀 Submit New Content'}
        </DialogTitle>
        
        <DialogContent>
          {submissionSuccess ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {isPrivate ? 'Kept Secret' : 'Thank you!'}
              </Typography>
              <Typography sx={{ mb: 4, opacity: 0.8 }}>
                {isPrivate 
                  ? "Your content has been safely locked away in your Private Library." 
                  : "Your content has been submitted and is pending review."}
              </Typography>
              
              <ActionButton 
                darkMode={darkMode} 
                variant="contained" 
                fullWidth 
                onClick={() => {
                  setDialogOpen(false);
                  // Dinamikus átirányítás attól függően, hogy privát vagy sem
                  navigate(isPrivate ? '/myprivatecontents' : '/mycontents');
                }}
              >
                {isPrivate ? 'Take me to my Private Vault' : 'View My Contents'}
              </ActionButton>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {submissionError && <Alert severity="error" sx={{ borderRadius: '12px' }}>{submissionError}</Alert>}
              
              <StyledTextField 
                darkMode={darkMode} label="Title" fullWidth placeholder="Give it a catchy title..."
                value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
              />
              
              <StyledTextField 
                darkMode={darkMode} label="Category" fullWidth placeholder="e.g. Programming, Biology"
                value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} 
              />
              
              <StyledTextField 
                darkMode={darkMode} label="Short Description" fullWidth multiline rows={3} placeholder="What is this about?"
                value={formData.shortDescription} onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })} 
              />
              
              <StyledTextField 
                darkMode={darkMode} 
                label="URL" 
                fullWidth 
                placeholder="https://example.com"
                value={formData.url} 
                error={urlError}
                helperText={urlError ? "Please enter a valid URL (e.g. https://google.com)" : ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData({ ...formData, url: val });
                  if (val === "") setUrlError(false);
                  else setUrlError(!isValidUrl(val));
                }} 
              />
              
              <Box sx={{ p: 2, borderRadius: '16px', bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.4)', border: '1px solid rgba(128,128,128,0.1)' }}>
                <FormControlLabel 
                  control={<Switch checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} color="secondary" />} 
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Private Content</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>Only you will see this in your Private Library.</Typography>
                    </Box>
                  } 
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        {!submissionSuccess && (
          <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ color: darkMode ? 'white' : 'inherit', opacity: 0.7 }}>Cancel</Button>
            <ActionButton 
              darkMode={darkMode} onClick={handleSubmitContent} variant="contained" 
              startIcon={<CloudUploadIcon />}
              disabled={!formData.title || !formData.url || urlError}
            >
              Submit Content
            </ActionButton>
          </DialogActions>
        )}
      </AnimatedDialog>
    </>
  );
};

export default LeftPanel;